import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import type { GenieBuildBoardEntryPoint } from 'webapp/enums/ai-chat';
import { BillingFeature } from 'webapp/enums/enums';

import type { CategoryInfo } from 'webapp/components/board/popup-select-category';
import SubmitButton from 'webapp/components/submit-button';

import type Board from 'webapp/models-custom/board';
import type DatawallGrid from 'webapp/models-custom/datawall-grid';

interface Args {
	entryPoint: GenieBuildBoardEntryPoint;
	model?: Board;
	activeGridWall?: DatawallGrid | null;
	text?: string;
	isDisabled?: boolean;
}

interface CommonAutoCreateOrCompleteBoardButtonComponentInterface<T> {
	Element: HTMLAnchorElement;
	Args: T;
}

export default class CommonAutoCreateOrCompleteBoardButtonComponent extends Component<
	CommonAutoCreateOrCompleteBoardButtonComponentInterface<Args>
> {
	@service('account') declare accountService: Service.Account;
	@service('billing') declare billingService: Service.Billing;
	@service('flow/genie-board-build')
	declare genieBoardBuildService: Service.Flow.GenieBoardBuild;

	get isShown() {
		return this.genieBoardBuildService.isAvailable;
	}

	get text(): string {
		return this.args.text ?? 'Create with AI';
	}

	get tooltipText(): string {
		if (!this.args.isDisabled) {
			return '';
		}

		return `
			<div>
				<strong>AI-completion unavailable</strong>
				<p class="text-gray">Your dashboard is fully populated, or no AI-generated metrics are available.</p>
			</div>
		`;
	}

	@action
	populateBoard() {
		if (this.accountService.handleDemo() || !this.genieBoardBuildService.checkCanBuild()) {
			return;
		}

		const { model, entryPoint } = this.args;

		if (model && !model.isNew) {
			const emptySlots = this.genieBoardBuildService.countEmptyGridPlaces(
				this.args.activeGridWall ?? null,
			);
			if (this.genieBoardBuildService.hasNoMetricBlocks(model)) {
				this.genieBoardBuildService.openCategoryPopup((category: CategoryInfo) => {
					this.genieBoardBuildService.completeBoard(model, {
						category,
						emptySlots,
						entryPoint,
					});
				});
			} else {
				this.genieBoardBuildService.completeBoard(model, { emptySlots, entryPoint });
			}

			return;
		}

		if (!this.billingService.checkIfCan(BillingFeature.BOARDS, true)) {
			return;
		}

		this.genieBoardBuildService.openCategoryPopup((category: CategoryInfo) => {
			// Passing color when in designer to prevent color change.
			void this.genieBoardBuildService.createBoardAndBuild({
				color: model?.color,
				category,
				entryPoint,
			});
		});
	}

	<template>
		{{#if this.isShown}}
			<SubmitButton
				@onTrigger={{this.populateBoard}}
				@text={{this.text}}
				@theme="purple-gradient"
				@isDisabled={{@isDisabled}}
				@submitInProgress={{this.genieBoardBuildService.isOpening}}
				@noDelay={{true}}
				@ripplerEffect={{false}}
				@tooltipText={{this.tooltipText}}
				@extraClass="complete-with-ai"
				@icon="Ai"
			/>
		{{/if}}
	</template>
}
